import React from "react";
import { CheckCircle2, Circle, Clock, Coins, Send, Hammer } from "lucide-react";

/**
 * Sovereign Strike — 5-stage lifecycle visualisation.
 *
 * Director directive 2026.02 canonical lifecycle:
 *   commissioned → deployed → active → verified → gold_released
 *
 * Renders horizontally with colour-coded waypoints. The active stage glows.
 * Completed stages are filled. Future stages are muted.
 *
 * Usage: <LifecycleTimeline stage="active" />
 */

const STAGES = [
    { key: "commissioned", label: "Commissioned", icon: Send, hint: "Delivery posted by client" },
    { key: "deployed", label: "Deployed", icon: Clock, hint: "Operative accepted" },
    { key: "active", label: "Active", icon: Hammer, hint: "Delivery in execution" },
    { key: "verified", label: "Verified", icon: CheckCircle2, hint: "Client confirmed completion" },
    { key: "gold_released", label: "Gold Released", icon: Coins, hint: "Payout dispersed" },
];

// Legacy → canonical mapper (mirrors backend `canonical_stage()`).
const ALIASES = {
    pending: "commissioned",
    accepted: "deployed",
    in_progress: "active",
    on_hold: "deployed",
    completed: "verified",
};

function canonical(s) {
    if (!s) return "commissioned";
    const lower = String(s).toLowerCase();
    if (STAGES.some((x) => x.key === lower)) return lower;
    return ALIASES[lower] || lower;
}

export default function LifecycleTimeline({ stage, compact = false }) {
    const current = canonical(stage);
    const currentIdx = Math.max(0, STAGES.findIndex((s) => s.key === current));

    return (
        <div className="w-full" data-testid="lifecycle-timeline">
            <div className="flex items-center">
                {STAGES.map((s, i) => {
                    const Icon = s.icon;
                    const done = i < currentIdx;
                    const live = i === currentIdx;
                    const cellColor = done
                        ? "bg-[hsl(153_50%_30%)] text-[hsl(153_60%_85%)] border-[hsl(153_55%_50%)]"
                        : live
                        ? "bg-[hsl(40_61%_25%)] text-[hsl(45_85%_75%)] border-[hsl(45_85%_60%)] shadow-[0_0_12px_hsl(40_70%_45%/0.6)]"
                        : "bg-[hsl(151_55%_10%)] text-muted-foreground border-[hsl(40_30%_25%/0.5)]";
                    const lineColor = done ? "bg-[hsl(153_50%_45%)]" : "bg-[hsl(40_30%_25%/0.4)]";
                    return (
                        <React.Fragment key={s.key}>
                            <div className="flex flex-col items-center" data-testid={`lifecycle-stage-${s.key}`}>
                                <div className={`w-8 h-8 rounded-full border flex items-center justify-center transition-all ${cellColor}`}>
                                    {done ? <CheckCircle2 size={14} strokeWidth={2} /> : <Icon size={13} strokeWidth={1.6} />}
                                </div>
                                {!compact && (
                                    <div className="mt-2 text-center">
                                        <div className={`text-[9px] uppercase tracking-[0.2em] ${live ? "text-[hsl(45_85%_75%)] font-semibold" : done ? "text-[hsl(153_60%_70%)]" : "text-muted-foreground"}`}>
                                            {s.label}
                                        </div>
                                    </div>
                                )}
                            </div>
                            {i < STAGES.length - 1 && (
                                <div className={`flex-1 h-0.5 mx-1 mb-6 transition-colors ${lineColor}`} />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * Priority badge — high_mass / high_yield / standard.
 */
export function PriorityBadge({ priority, className = "" }) {
    const meta = {
        high_mass:  { label: "High Mass",  bg: "hsl(40 70% 30%)",  fg: "hsl(45 90% 78%)",  border: "hsl(45 85% 55%)", emoji: "Σ" },
        high_yield: { label: "High Yield", bg: "hsl(153 55% 25%)", fg: "hsl(153 65% 80%)", border: "hsl(153 60% 50%)", emoji: "≡" },
        standard:   { label: "Standard",   bg: "hsl(151 40% 16%)", fg: "hsl(45 30% 70%)",  border: "hsl(40 25% 30%)", emoji: "·" },
    }[priority] || null;
    if (!meta) return null;
    return (
        <span
            data-testid={`priority-badge-${priority}`}
            className={`inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full border font-semibold ${className}`}
            style={{ background: meta.bg, color: meta.fg, borderColor: meta.border }}
            title={`${meta.label} priority`}
        >
            <span className="font-mono opacity-80">{meta.emoji}</span>
            {meta.label}
        </span>
    );
}
